import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SiaPdfViewerComponent } from '@sia/pdf';
import { getFileTypeAllowed, SafeHtmlPipe } from '@sia/shared';
import Mark from 'mark.js';
import { ExcelPreview, NATIVE_IMAGE_EXTENSIONS } from '../../interfaces/file-preview.interface';
import { FilePreviewService } from '../../services/file-preview.service';
import { FilePreviewEmailComponent } from '../file-preview-email/file-preview-email.component';
import { FilePreviewExcelComponent } from '../file-preview-excel/file-preview-excel.component';
import { FilePreviewImageComponent } from '../file-preview-image/file-preview-image.component';
import { FilePreviewWordComponent } from '../file-preview-word/file-preview-word.component';

@Component({
  selector: 'sia-file-preview-handler',
  host: {
    class: 'flex flex-col h-full w-full',
  },
  imports: [
    CommonModule,
    FilePreviewImageComponent,
    FilePreviewEmailComponent,
    FilePreviewExcelComponent,
    SiaPdfViewerComponent,
    FilePreviewWordComponent,
    MatButtonModule,
    MatIconButton,
    MatIcon,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SafeHtmlPipe,
    FormsModule,
  ],
  templateUrl: './file-preview-handler.component.html',
  styleUrl: './file-preview-handler.component.css',
  providers: [FilePreviewService],
})
export class FilePreviewHandlerComponent {
  #filePreviewService = inject(FilePreviewService);
  #cdr = inject(ChangeDetectorRef);

  isFullscreenMode = model<boolean>(false);
  showFileName = input<boolean>(false);
  showOcr = input<boolean>(false);
  showFullscreen = input<boolean>(true);
  horizontal = input<boolean>(false);
  fileType = input<string | undefined>();
  previewFileType = computed(() => getFileTypeAllowed(this.fileType(), this.fileName()));
  fileId = input<string>('');
  fileName = input<string>('');
  loadingPreview = output<boolean>();

  isLoading = signal<boolean>(false);

  imagesUrl = signal<string[]>([]);
  pdfBlob = signal<Blob | null>(null);
  wordBlob = signal<Blob | null>(null);
  excelSheets = signal<ExcelPreview>({});
  emailBodyHtml = signal<string>('');
  textHtml = signal<string>('');

  isPdfPreview = computed(() => this.previewFileType() === 'pdf');
  isExcelPreview = computed(() => this.previewFileType() === 'xls' || this.previewFileType() === 'xlsx');
  isWordPreview = computed(() => this.previewFileType() === 'doc' || this.previewFileType() === 'docx');
  isNativeImagePreview = computed(() => NATIVE_IMAGE_EXTENSIONS.includes(this.previewFileType()));
  isEmailPreview = computed(() => this.previewFileType() === 'eml' || this.previewFileType() === 'msg');
  isTextPreview = computed(() => this.previewFileType() === 'text');

  canDownload = computed(() => !!this.fileId());
  previewLoaded = signal<boolean>(false);
  isOcrExpanded = signal<boolean>(false);
  ocrTextContent = signal<string>('');

  async toggleOcr() {
    this.isOcrExpanded.update((value) => !value);
    if (this.isOcrExpanded() && !this.ocrTextContent()) {
      this.loadingPreview.emit(true);
      try {
        if (this.fileId()) {
          const text = await this.#filePreviewService.retrieveTextContent(this.fileId());
          this.ocrTextContent.set(text || 'Nessun testo estratto.');
        }
      } catch (e) {
        this.ocrTextContent.set('Errore durante il recupero del testo.');
      } finally {
        this.loadingPreview.emit(false);
      }
    }
  }

  toggleFullscreen() {
    this.isFullscreenMode.set(!this.isFullscreenMode());
  }

  downloadFile() {
    const sha256 = this.fileId();
    let name = this.fileName();
    if (name && !name.includes('.')) {
      const fileType = this.fileType();
      if (fileType) {
        name = name + '.' + fileType;
      }
    }
    if (sha256) {
      this.#filePreviewService.downloadFile(sha256, name || 'download');
    }
  }

  @ViewChild(SiaPdfViewerComponent) pdfViewer?: SiaPdfViewerComponent;

  searchText = signal<string>('');

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('previewContainer') previewContainer?: ElementRef<HTMLElement>;

  private marker?: Mark;
  private markMatches: HTMLElement[] = [];
  currentMatchIndex = signal<number>(0);
  totalMatches = signal<number>(0);

  clearSearch() {
    this.searchText.set('');
    this.performSearch(false, 'find');
    if (this.marker) {
      this.marker.unmark();
      this.totalMatches.set(0);
      this.currentMatchIndex.set(0);
    }
  }

  performSearch(previous: boolean = false, type: 'find' | 'findagain' = 'find') {
    if (this.isPdfPreview() && this.pdfViewer) {
      this.pdfViewer.search(this.searchText(), type, previous);
    } else {
      this.searchWithMarkJs(previous, type);
    }
  }

  private searchWithMarkJs(previous: boolean, type: 'find' | 'findagain') {
    if (typeof window === 'undefined' || !this.previewContainer) return;

    let markContext: HTMLElement | null = this.previewContainer.nativeElement;
    if (this.isEmailPreview()) {
      const iframe = this.previewContainer.nativeElement.querySelector('iframe');
      const body = iframe?.contentDocument?.body ?? null;
      if (!body) return;
      markContext = body;
    }

    if (type === 'find') {
      this.marker = undefined;
    }

    if (!this.marker) {
      this.marker = new Mark(markContext!);
    }

    if (type === 'find') {
      this.marker.unmark({
        done: () => {
          this.currentMatchIndex.set(0);
          this.totalMatches.set(0);
          this.markMatches = [];

          const query = this.searchText();
          if (!query) return;

          this.marker!.mark(query, {
            separateWordSearch: false,
            done: (totalMarks: number) => {
              this.totalMatches.set(totalMarks);
              if (totalMarks > 0) {
                this.markMatches = Array.from(markContext!.querySelectorAll('mark'));
                this.currentMatchIndex.set(previous ? totalMarks - 1 : 0);
                this.scrollToCurrentMatch();
              }
              this.#cdr.detectChanges();
            },
          });
        },
      });
    } else {
      if (this.totalMatches() > 0) {
        if (previous) {
          this.currentMatchIndex.update((v) => (v <= 0 ? this.totalMatches() - 1 : v - 1));
        } else {
          this.currentMatchIndex.update((v) => (v >= this.totalMatches() - 1 ? 0 : v + 1));
        }
        this.scrollToCurrentMatch();
      }
    }
  }

  private scrollToCurrentMatch() {
    const idx = this.currentMatchIndex();
    if (idx >= 0 && idx < this.markMatches.length) {
      this.markMatches.forEach((m) => {
        m.style.backgroundColor = 'yellow';
        m.style.color = 'black';
      });
      const currentMatch = this.markMatches[idx];
      currentMatch.style.backgroundColor = 'var(--mat-sys-primary, orange)';
      currentMatch.style.color = 'var(--mat-sys-on-primary, white)';
      currentMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  onSearchEnter() {
    this.performSearch(false, 'findagain');
  }

  onSearchChange() {
    this.performSearch(false, 'find');
  }

  onExcelSheetChanged() {
    if (this.searchText()) {
      setTimeout(() => {
        this.performSearch(false, 'find');
      }, 50);
    }
  }

  onWordLoaded() {
    if (this.searchText()) {
      setTimeout(() => {
        this.performSearch(false, 'find');
      }, 50);
    }
  }

  onPdfMatchesCountChange(data: { current: number; total: number }) {
    if (data) {
      if (typeof data.current === 'number') {
        this.currentMatchIndex.set(Math.max(0, data.current - 1));
      }
      if (typeof data.total === 'number') {
        this.totalMatches.set(data.total);
      }
      this.#cdr.detectChanges();
    }
  }

  #fileIdChanged = effect(async () => {
    this.fileId();
    this.fileName();
    this.fileType();
    untracked(async () => {
      try {
        this.searchText.set('');
        if (this.marker) {
          this.marker.unmark();
          this.marker = undefined;
        }
        this.markMatches = [];
        this.totalMatches.set(0);
        this.currentMatchIndex.set(0);
        this.ocrTextContent.set('');
        this.isOcrExpanded.set(false);
        this.isLoading.set(true);
        this.loadingPreview.emit(true);
        this.previewLoaded.set(false);
        // cleanup — revoca blob URL prima di resettare per evitare memory leak
        this.imagesUrl().forEach((url) => {
          if (url.startsWith('blob:')) URL.revokeObjectURL(url);
        });
        this.imagesUrl.set([]);
        this.pdfBlob.set(null);
        this.wordBlob.set(null);
        this.excelSheets.set({});
        this.emailBodyHtml.set('');
        this.textHtml.set('');

        if (!this.fileId() || !this.previewFileType()) {
          this.isLoading.set(false);
          this.loadingPreview.emit(false);
          return;
        }

        const previewData = await this.#filePreviewService.loadPreview(this.previewFileType()!, this.fileId());
        switch (previewData.type) {
          case 'pdf':
            if (previewData.pdfBlob) {
              this.pdfBlob.set(previewData.pdfBlob);
            } else {
              this.imagesUrl.set(previewData.images || []);
            }
            break;
          case 'doc':
            if (previewData.wordBlob) {
              this.wordBlob.set(previewData.wordBlob);
            } else {
              this.imagesUrl.set(previewData.images || []);
            }
            break;
          case 'image':
            this.imagesUrl.set(previewData.images || []);
            break;
          case 'excel':
            this.excelSheets.set(previewData.excelSheets || {});
            break;
          case 'email':
            this.emailBodyHtml.set(previewData.emailBodyHtml || '');
            break;
          case 'text':
            this.textHtml.set(previewData.textHtml || '');
            break;
        }
      } finally {
        this.isLoading.set(false);
        this.loadingPreview.emit(false);
        this.previewLoaded.set(true);
      }
    });
  });
}
